import React from "react";
import FormField from "../../../common/FormField/FormField";
import { Formik } from "formik";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { navigate } from "@reach/router";
import { useTheme, useMediaQuery, Popper } from "@material-ui/core";
import { renameImageValidationSchema } from "../../../../helpers/validationSchema";
import { StyledForm, ButtonGroup, BackButton, SubmitButton } from "./styles";
import AWSService from "../../../../services/AWSService";
import { getFileName, getErrorMessage } from "../../../../helpers/functions";
import {
  Card,
  FormGrid,
  Label,
  PageWrapper,
  Section,
  StyledAutocomplete,
} from "./SplitAndUpload";

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const RenameImage = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const themes = useTheme();
  const isBigScreen = useMediaQuery(themes.breakpoints.up(640));

  async function s3Upload(file, field, setFieldValue, setFieldError) {
    if (file) {
      try {
        setLoader({ state: true, message: `Uploading file ${file.name} ...` });
        const payload = {
          key: file.name,
        };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        setFieldError(field, message);
      } finally {
        setLoader({ state: false });
      }
    }
  }

  const inputs = [
    {
      name: "postInputText",
      type: "text",
      label: "Post Name",
    },
  ];

  const upload_templates = [
    {
      name: "excelFile",
      label: "Upload Xlsx",
      type: "upload",
      required: true,
      accept: ".xlsx",
      types: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ],
    },
    {
      name: "zipFilePath",
      label: "Zip File (zip)",
      type: "upload",
      required: true,
      accept: ".zip",
      types: ["application/zip"],
    },
  ];

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "downloading..." });
      let formData = new FormData();
      ["excelFile", "zipFilePath", "preInputText", "postInputText"].forEach(
        (key) => {
          if (values[key]) {
            formData.append(key, values[key]);
          }
        },
      );
      const response = await fetch(
        `https://staging-api.justact.co.in/rename-pdf`,
        {
          method: "POST",
          body: formData,
        },
      );
      if (response.ok) {
        const data = await response.json();
        enqueueSnackbar("Zip file created successfully", {
          variant: "success",
        });
        const a = document.createElement("a");
        a.href = data?.output_file_url;
        a.download = `${getFileName(data?.output_file_url)}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        resetForm();
      }
    } catch (error) {
      enqueueSnackbar(error?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            preInputText: "",
            postInputText: "",
            excelFile: "",
            zipFilePath: "",
          }}
          validationSchema={renameImageValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldError,
            resetForm,
          }) => (
            <StyledForm>
              <Section>
                <FormGrid>
                  <div>
                    <Label>Pre Name</Label>
                    <StyledAutocomplete
                      id="preInputText-autocomplete"
                      size="small"
                      options={menuItems}
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            onBlur={handleBlur}
                            error={
                              errors["preInputText"] && touched["preInputText"]
                            }
                            helperText={
                              touched["preInputText"] && errors["preInputText"]
                            }
                            placeholder="Select pre name..."
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                            }}
                          />
                        </div>
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>
                          <div
                            style={{
                              padding: "4px 0",
                              color: "#293461",
                              fontFamily: "openSans-SemiBold, sans-serif",
                            }}
                          >
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: "#949ab0",
                            fontFamily: "openSans-SemiBold, sans-serif",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setFieldValue("preInputText", newValue?.name || "")
                      }
                    />
                  </div>
                  <div style={{ marginRight: isBigScreen ? "10px" : "0px" }}>
                    {inputs.map((input, index) => (
                      <FormField
                        key={index}
                        {...{
                          input,
                          values,
                          handleBlur,
                          handleChange,
                          touched,
                          errors,
                        }}
                      />
                    ))}
                  </div>
                </FormGrid>
              </Section>
              <Section>
                <FormGrid>
                  {upload_templates.map((upload, index) => (
                    <div style={{ marginTop: 10 }}>
                      <Label>
                        {upload.label} <span className="required-star"> *</span>
                      </Label>
                      <CustomFileUpload
                        error={touched[upload.name] && errors[upload.name]}
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            setFieldError,
                          )
                        }
                        style={{
                          width: "100%",
                        }}
                        accept={upload.accept}
                        file={{
                          name: getFileName(values[upload.name]),
                        }}
                      />
                    </div>
                  ))}
                </FormGrid>
              </Section>
              <ButtonGroup>
                <BackButton onClick={() => navigate("/dashboard/case-manager")}>
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

export default RenameImage;
